import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cancelLeaveRequest } from '../../../store/slices/leaveManagementSlice';
import { format } from 'date-fns';
import './LeaveDetailModal.scss';

const LeaveDetailModal = ({ leave, onClose, onEdit }) => {
  const dispatch = useDispatch();
  const { cancellingLeave, cancelLeaveError } = useSelector(state => state.leaveManagement);
  
  // Format date to readable format
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return format(date, 'MMMM d, yyyy');
  };
  
  // Get status badge class based on leave status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Approved':
        return 'status-badge approved';
      case 'Rejected':
        return 'status-badge rejected';
      case 'Pending':
        return 'status-badge pending';
      case 'Cancelled':
        return 'status-badge cancelled';
      default:
        return 'status-badge';
    }
  };
  
  // Get display leave type
  const getDisplayLeaveType = (type) => {
    if (type === 'Vacation') return 'Vacation';
    if (type === 'Sick') return 'Sick Leave';
    return type;
  };
  
  const handleCancelRequest = () => {
    if (window.confirm('Are you sure you want to cancel this leave request?')) {
      dispatch(cancelLeaveRequest(leave.id))
        .unwrap()
        .then(() => {
          onClose();
        })
        .catch((err) => {
          console.error('Failed to cancel leave request:', err);
        });
    }
  };
  
  return (
    <div className="modal-overlay">
      <div className="leave-detail-modal">
        <div className="modal-header">
          <h3>Leave Request Details</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <div className="modal-body">
          <div className="leave-detail-row">
            <div className="detail-label">Status</div>
            <div className="detail-value">
              <span className={getStatusBadgeClass(leave.status)}>
                {leave.status}
              </span>
            </div>
          </div>
          
          <div className="leave-detail-row">
            <div className="detail-label">Leave Type</div>
            <div className="detail-value">{getDisplayLeaveType(leave.leaveType)}</div>
          </div>
          
          <div className="leave-detail-row">
            <div className="detail-label">From Date</div>
            <div className="detail-value">{formatDate(leave.startDate)}</div>
          </div>
          
          <div className="leave-detail-row">
            <div className="detail-label">To Date</div>
            <div className="detail-value">{formatDate(leave.endDate)}</div>
          </div>
          
          <div className="leave-detail-row">
            <div className="detail-label">Number of Days</div>
            <div className="detail-value">{leave.leaveDays}</div>
          </div>
          
          {leave.preLeaveBalance !== undefined && (
            <div className="leave-detail-row">
              <div className="detail-label">Previous Balance</div>
              <div className="detail-value">{leave.preLeaveBalance} days</div>
            </div>
          )}
          
          <div className="leave-detail-row remarks">
            <div className="detail-label">Remarks</div>
            <div className="detail-value">
              {leave.leaveRemarks ? (
                <div className="remarks-content">{leave.leaveRemarks}</div>
              ) : (
                <em className="no-remarks">No remarks provided</em>
              )}
            </div>
          </div>
          
          {leave.managerRemarks && (
            <div className="leave-detail-row manager-remarks">
              <div className="detail-label">Manager's Remarks</div>
              <div className="detail-value">{leave.managerRemarks}</div>
            </div>
          )}
          
          {cancelLeaveError && (
            <div className="error-alert">
              Failed to cancel request: {cancelLeaveError}
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button 
            className="close-btn"
            onClick={onClose}
          >
            Close
          </button>
          
          {leave.status === 'Pending' && (
            <>
              <button 
                className="edit-btn"
                onClick={() => onEdit(leave)}
              >
                Edit Remarks
              </button>
              
              <button 
                className="cancel-request-btn"
                onClick={handleCancelRequest}
                disabled={cancellingLeave}
              >
                {cancellingLeave ? (
                  <>
                    <span className="spinner"></span>
                    Cancelling...
                  </>
                ) : 'Cancel Request'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveDetailModal; 